import { useTranslation } from "react-i18next";
import { ShieldCheck, LockSimple, Lightning } from "@phosphor-icons/react";
import { useScrollReveal } from "../hooks/useScrollReveal";

const HeroSection = () => {
  const [ref, isVisible] = useScrollReveal();
  const { t } = useTranslation();

  return (
    <section className="relative lg:min-h-screen flex items-center overflow-hidden pt-20 pb-6 lg:pb-0" id="hero">

      <style>{`
        .hero-bg {
          background: #030305;
          position: absolute;
          inset: 0;
        }
        .hero-bg::after {
          content: "";
          position: absolute;
          top: 40%;
          left: 55%;
          width: 900px;
          height: 900px;
          transform: translate(-50%, -50%);
          background: radial-gradient(circle, rgba(60,20,120,0.15) 0%, rgba(20,5,60,0.06) 40%, transparent 65%);
          pointer-events: none;
        }
      `}</style>

      {/* Layer 0: Background */}
      <div className="hero-bg" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10 w-full">
        <div className="grid lg:grid-cols-[42%_58%] gap-6 lg:gap-2 items-center">

          {/* ===== LEFT: TEXT ===== */}
          <div ref={ref} className="relative z-20 space-y-5">

            <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full border border-violet-400/40 bg-violet-500/[0.1]" data-testid="hero-badge"
              style={{ boxShadow: '0 0 20px rgba(139,92,246,0.2), inset 0 0 15px rgba(139,92,246,0.05)' }}>
              <ShieldCheck size={20} weight="duotone" className="text-violet-400" style={{ filter: 'drop-shadow(0 0 8px rgba(139,92,246,0.8))' }} />
              <span className="text-violet-300 text-sm sm:text-base font-bold tracking-[0.12em] uppercase"
                style={{ textShadow: '0 0 15px rgba(139,92,246,0.6)' }}>
                {t("hero.badge")}
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl xl:text-[4.5rem] font-black leading-[0.9] tracking-tight" data-testid="hero-title">
              <span className="text-white block">{t("hero.title1")}</span>
              <span className="block bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, #a855f7 0%, #9333ea 40%, #e879f9 100%)' }}>
                {t("hero.title2")}
              </span>
            </h1>

            <p className="text-gray-400 text-base sm:text-lg max-w-md leading-relaxed" data-testid="hero-description">
              {t("hero.description")}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 pt-1">
              <a href="https://play.google.com/store/apps/details?id=com.luminawallet.app" target="_blank" rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 px-5 py-3 rounded-xl text-white font-semibold text-sm transition-all duration-300 hover:scale-[1.02] border border-white/15 bg-white/[0.06] hover:bg-white/[0.1] hover:border-violet-500/40"
                style={{ boxShadow: '0 0 15px rgba(124,58,237,0.1)' }}
                data-testid="hero-google-play-btn" data-umami-event="download-gplay-click">
                <svg viewBox="0 0 512 512" className="w-6 h-6 flex-shrink-0">
                  <path d="M270.1 257.3L34.4 501.7c-2.7-4.4-4.4-9.5-4.4-15.2V25.5c0-5.7 1.6-10.8 4.4-15.2l235.7 247z" fill="#4DB6AC"/>
                  <path d="M366.4 332.7L270.1 257.3 34.4 11.7c6.9-11.2 19.7-15.5 31.9-8.5l300.1 173.3-0 156.2z" fill="#DCE775"/>
                  <path d="M366.4 332.7L270.1 257.3l96.3-80.8 110.3 63.6c17.8 10.3 17.8 36.1 0 46.4L366.4 332.7z" fill="#F44336"/>
                  <path d="M366.4 332.7l-300.1 173.3c-12.2 7-25-2.7-31.9-8.5l235.7-240.2 96.3 75.4z" fill="#F06292"/>
                </svg>
                <div className="text-left">
                  <span className="text-[10px] text-gray-400 leading-none block">Google Play</span>
                  <span className="text-sm font-bold leading-tight">Android</span>
                </div>
              </a>
              <a href="https://apps.apple.com/us/app/lumina-wallet/id6760651380" target="_blank" rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 px-5 py-3 rounded-xl text-white font-semibold text-sm transition-all duration-300 hover:scale-[1.02] border border-white/15 bg-white/[0.06] hover:bg-white/[0.1] hover:border-violet-500/40"
                style={{ boxShadow: '0 0 15px rgba(124,58,237,0.1)' }}
                data-testid="hero-app-store-btn" data-umami-event="download-appstore-click">
                <svg viewBox="0 0 24 24" className="w-6 h-6 flex-shrink-0" fill="white">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="text-left">
                  <span className="text-[10px] text-gray-400 leading-none block">App Store</span>
                  <span className="text-sm font-bold leading-tight">iOS</span>
                </div>
              </a>
            </div>
          </div>

          {/* ===== RIGHT: VISUAL SCENE (image from mockup) ===== */}
          <div>
            <div className="relative flex items-center justify-center" data-testid="hero-phones">
              <img
                src="/images/hero-visual.webp"
                alt="Lumina Wallet - Anti-Scam Protection"
                className="w-full max-w-2xl h-auto"
                width="1024"
                height="768"
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </div>
          </div>
        </div>

        {/* Trust bar */}
        <div className="hidden md:flex flex-col sm:flex-row items-start sm:items-center justify-center gap-4 sm:gap-0 mt-10 md:mt-14 pt-6 border-t border-white/[0.06]" data-testid="hero-trust-bar">
          {[
            { icon: ShieldCheck, label: t("hero.trustBar.detectsMalicious") },
            { icon: LockSimple, label: t("hero.trustBar.blocksAccess") },
            { icon: Lightning, label: t("hero.trustBar.alertsInstantly") }
          ].map((item, i) => (
            <div key={item.label} className="flex items-center">
              {i > 0 && <div className="hidden sm:block w-px h-4 bg-white/[0.08] mx-8" />}
              <div className="flex items-center gap-2.5">
                <item.icon size={16} weight="duotone" className="text-violet-400" style={{ filter: 'drop-shadow(0 0 6px rgba(139,92,246,0.7))' }} />
                <span className="text-gray-400 text-sm">{item.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
